import React, { FC, Fragment, useContext } from 'react';
import { some } from 'lodash';
import { Button, ButtonProps } from '@material-ui/core';
import { makeStyles, darken } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Network, SocialAccount } from 'api';
import { CustomSVG } from 'components';
import { SelectedNetworksContext } from 'providers';
import { connectionPopup, getSocialAccountName } from 'helpers';
import { useCurrentUser, useFeatures, useSocialConnections } from 'hooks';

import { FACEBOOK_ID, FACEBOOK_DISPLAY, TWITTER_DISPLAY, LINKEDIN_DISPLAY } from '../../constants';

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: 4,
    textTransform: 'capitalize',
  },
  outlinedButton: {
    border: `1px solid ${theme.custom.palette.grey3}`,
    color: theme.custom.palette.grey1,
  },
  inactiveIcon: {
    color: theme.custom.palette.grey2,
  },
  activeColor: {
    color: theme.palette.common.white,
  },
  twitter: {
    borderColor: 'transparent',
    backgroundColor: theme.custom.networks.twitter,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.twitter, 0.1),
    },
  },
  linkedin: {
    borderColor: 'transparent',
    backgroundColor: theme.custom.networks.linkedin,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
    },
  },
  facebook: {
    borderColor: 'transparent',
    backgroundColor: theme.custom.networks.facebook,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.facebook, 0.1),
    },
  },
}));

const Connections: FC = () => {
  const { selectedNetworks, toggleNetwork } = useContext(SelectedNetworksContext);
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const connections = useSocialConnections();
  const { legacyAuthDomain } = useFeatures();

  const twitterConnections = connections.filter(conn => conn.provider === Network.Twitter);
  const linkedinConnections = connections.filter(conn => conn.provider === Network.Linkedin);
  const hasTwitter = !!twitterConnections.length;
  const hasLinkedIn = !!linkedinConnections.length;

  function getProps(isActive: boolean, provider: string) {
    const activeClasses = {
      variant: 'contained' as ButtonProps['variant'],
      classes: { contained: classNames(classes[provider], classes.activeColor) },
    };
    const inactiveClasses = {
      variant: 'outlined' as ButtonProps['variant'],
      classes: { outlined: classes.outlinedButton },
    };

    return isActive ? activeClasses : inactiveClasses;
  }

  function renderFacebook() {
    const isActive = selectedNetworks.includes(FACEBOOK_ID);

    const commonProps = {
      className: classes.button,
      onClick: () => toggleNetwork(FACEBOOK_ID),
      ...getProps(isActive, FACEBOOK_ID),
    };

    return (
      <Button
        startIcon={
          <CustomSVG
            size={24}
            name={FACEBOOK_ID}
            className={classNames({
              [classes.activeColor]: isActive,
              [classes.inactiveIcon]: !isActive,
            })}
          />
        }
        {...commonProps}>
        {FACEBOOK_DISPLAY}
      </Button>
    );
  }

  function renderConnection(connection: SocialAccount, isActive: boolean) {
    const commonProps = {
      key: connection.id,
      className: classes.button,
      onClick: () => toggleNetwork(connection),
      ...getProps(isActive, connection.provider),
    };

    return (
      <Button
        startIcon={
          <CustomSVG
            size={24}
            name={connection.provider as any}
            className={classNames({
              [classes.activeColor]: isActive,
              [classes.inactiveIcon]: !isActive,
            })}
          />
        }
        {...commonProps}>
        {getSocialAccountName(connection)}
      </Button>
    );
  }

  function renderConnectTwitter() {
    const commonProps = {
      key: 'twitter',
      className: classes.button,
      onClick: () => connectionPopup(currentUser.id, 'twitter', legacyAuthDomain),
      ...getProps(false, 'twitter'),
    };

    return (
      <Button
        startIcon={<CustomSVG size={24} name="twitter" className={classes.inactiveIcon} />}
        {...commonProps}>
        {TWITTER_DISPLAY}
      </Button>
    );
  }

  function renderConnectLinkedin() {
    const commonProps = {
      key: 'linkedin',
      className: classes.button,
      onClick: () => connectionPopup(currentUser.id, 'linkedin', legacyAuthDomain),
      ...getProps(false, 'linkedin'),
    };

    return (
      <Button
        startIcon={<CustomSVG size={24} name="linkedin" className={classes.inactiveIcon} />}
        {...commonProps}>
        {LINKEDIN_DISPLAY}
      </Button>
    );
  }

  return (
    <Fragment>
      {twitterConnections.map(connection => {
        const { id, expires_soon } = connection;
        const isActive = some(selectedNetworks, ['id', id]);
        return expires_soon ? renderConnectTwitter() : renderConnection(connection, isActive);
      })}
      {!hasTwitter && renderConnectTwitter()}
      {linkedinConnections.map(connection => {
        const { id, expires_soon } = connection;
        const isActive = some(selectedNetworks, ['id', id]);
        return expires_soon ? renderConnectLinkedin() : renderConnection(connection, isActive);
      })}
      {!hasLinkedIn && renderConnectLinkedin()}
      {renderFacebook()}
    </Fragment>
  );
};

export default Connections;
